import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiEye, FiTrash2 } from 'react-icons/fi';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const MiscList = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => { loadShipments(); }, [statusFilter, searchQuery]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/misc');
      let data = res.data || [];
      if (statusFilter) data = data.filter(s => s.status === statusFilter);
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        data = data.filter(s =>
          (s.file_no || '').toLowerCase().includes(q) ||
          (s.mbl_no || '').toLowerCase().includes(q) ||
          (s.hbl_no || '').toLowerCase().includes(q) ||
          (s.customer || '').toLowerCase().includes(q) ||
          (s.shipper || '').toLowerCase().includes(q) ||
          (s.consignee || '').toLowerCase().includes(q) ||
          (s.vessel_flight_no || '').toLowerCase().includes(q) ||
          (s.container_no || '').toLowerCase().includes(q)
        );
      }
      setShipments(data);
    } catch (error) {
      toast.error('Failed to load operations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/misc/${id}`);
      toast.success('Operation deleted');
      setShipments(shipments.filter(s => s.id !== id));
      setConfirmDelete(null);
    } catch (error) {
      toast.error('Failed to delete operation');
    }
  };

  const columns = [
    {
      key: 'file_no',
      label: 'File No.',
      sortable: true,
      render: (value, row) => (
        <Link
          to={`/misc/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
        >
          {value}
        </Link>
      ),
    },
    {
      key: 'post_date', label: 'Post Date', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      key: 'mbl_no',
      label: 'MB/L No.',
      sortable: true,
      render: (value, row) => (
        <Link
          to={`/misc/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
        >
          {value}
        </Link>
      ),
    },
    { key: 'hbl_no', label: 'HB/L No.', sortable: true },
    {
      key: 'etd', label: 'ETD', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      key: 'eta', label: 'ETA', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    { key: 'vessel_flight_no', label: 'Vessel/Flight', sortable: true },
    { key: 'shipper', label: 'Shipper', sortable: true },
    { key: 'consignee', label: 'Consignee', sortable: true },
    {
      key: 'svc_term', label: 'SVC Term', sortable: true,
      render: (_, row) => `${row.service_term_from || 'CY'} - ${row.service_term_to || 'CY'}`,
    },
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
  ];

  const renderActions = (row) => [
    <button key="view" className="btn btn-sm btn-primary" onClick={() => navigate(`/misc/${row.id}`)} title="View">
      <FiEye /> VIEW
    </button>,
    <button key="delete" className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(row)} title="Delete">
      <FiTrash2 />
    </button>,
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">MY OPERATION LIST</h2>
          <button className="btn btn-primary" onClick={() => navigate('/misc/new')}>
            <FiPlus /> New Operation
          </button>
        </div>
        <div className="filter-bar">
          <div className="filter-group">
            <label className="filter-label">Status:</label>
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          <div className="filter-group" style={{ flex: 1, marginLeft: 'auto' }}>
            <input type="text" className="filter-input" placeholder="Search by file no, MB/L, HB/L, customer, shipper, consignee..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>
        <DataTable columns={columns} data={shipments} loading={loading} renderActions={renderActions} pageSize={15} />
      </div>
      <ConfirmDialog isOpen={!!confirmDelete} title="Delete Operation" message={`Are you sure you want to delete operation ${confirmDelete?.file_no}?`} isDangerous onConfirm={() => handleDelete(confirmDelete.id)} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
};

export default MiscList;
